import { useCallback, useEffect, useMemo, useState } from 'react'

import { admin } from '../../lib/api/admin.ts'
import { machines } from '../../lib/api/resources.ts'
import type {
  AgentCommand,
  CommandCatalog,
  CommandDefinition,
} from '../../lib/api/admin.ts'
import { adminPageProps } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { formatDayClock, formatMinutes } from '../../lib/format.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { navigate } from '../../lib/router.tsx'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField } from '../primitives/Field.tsx'
import { Pagination } from '../primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { Tooltip } from '../primitives/Tooltip.tsx'
import { useToast } from '../primitives/Toast.tsx'
import type { Tone } from '../primitives/Metric.tsx'
import { CommandStateBadge } from '../commands/CommandState.tsx'
import { cidrProblem, ipProblem, isIpCommandKind } from '../../lib/ip/semantics.ts'
import {
  COMMAND_POLL_MS,
  failureText,
  isAwaitingAgent,
  isConfirmed,
  openCommandIdOf,
  unavailableReasonText,
} from '../../lib/commands/semantics.ts'

const SEVERITY_TONE: Record<string, Tone> = {
  Critical: 'crit',
  High: 'warn',
  Medium: 'accent',
  Low: 'neutral',
}

/**
 * Centro de controle do agente (§6).
 *
 * ⚠️ São entregues ao agente: desbloqueio (`restore_host`), comandos de IP e
 * `collect_artifact`. Isolar, cortar e restaurar internet continuam sem entrega:
 * o catálogo os marca `kind_not_deliverable` e o botão explica por quê. Entregue
 * também não é feito — a situação lê `state`, e só `executed`/`skipped` são
 * confirmação.
 */
export function ControlCenter({
  endpointId,
  onClose,
}: {
  endpointId: string
  onClose: () => void
}) {
  const fetcher = useCallback(
    (signal: AbortSignal) => admin.commandCatalog(endpointId, signal),
    [endpointId],
  )
  const { data: catalog, error, loading, reload } = useResource<CommandCatalog>(fetcher)

  // Reabre a listagem depois de emitir/cancelar sem refazer o catálogo.
  const [historyNonce, setHistoryNonce] = useState(0)
  const [selected, setSelected] = useState<CommandDefinition | null>(null)

  const definitions = useMemo(() => {
    const map = new Map<string, CommandDefinition>()
    for (const def of catalog?.commands ?? []) map.set(def.kind, def)
    return map
  }, [catalog])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title="Centro de Controle" wide>
      <div className="space-y-4">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : catalog === null ? (
          <LoadingRow label="Carregando o catálogo…" />
        ) : (
          <>
            {/* Vem PRONTO do servidor, em português — exiba literalmente (§6.4).
                Existe para que a tela não POSSA esquecer de avisar. */}
            {catalog.dispatch.warning !== null && (
              <Notice tone="crit">{catalog.dispatch.warning}</Notice>
            )}

            <section>
              <h3 className="mb-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
                Comandos
              </h3>
              {/* Os botões vêm do catálogo; nada é hardcoded (§6.1). */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {catalog.commands.map((def) => (
                  <CommandButton key={def.kind} def={def} onPick={() => setSelected(def)} />
                ))}
              </div>
            </section>

            <CommandHistory
              endpointId={endpointId}
              definitions={definitions}
              reloadKey={historyNonce}
              onUndo={(def) => setSelected(def)}
            />
          </>
        )}
      </div>

      {loading && catalog !== null && (
        <span className="mt-2 block text-micro text-fg-muted">atualizando…</span>
      )}

      {selected !== null && catalog !== null && (
        <IssueCommandDialog
          endpointId={endpointId}
          definition={selected}
          warning={catalog.dispatch.warning}
          onClose={() => setSelected(null)}
          onIssued={() => {
            setSelected(null)
            setHistoryNonce((n) => n + 1)
          }}
        />
      )}
    </Dialog>
  )
}

function CommandButton({ def, onPick }: { def: CommandDefinition; onPick: () => void }) {
  const tone = SEVERITY_TONE[def.severity] ?? 'neutral'

  const button = (
    <button
      type="button"
      disabled={!def.available}
      onClick={onPick}
      title={def.available ? def.description : undefined}
      className="flex w-full flex-col items-start gap-1 border border-line-strong px-2.5 py-2 text-left hover:bg-inset disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="text-sm text-fg">{def.label}</span>
        <Badge tone={tone}>{def.severity}</Badge>
      </span>
      <span className="line-clamp-2 text-micro leading-snug text-fg-muted">{def.description}</span>
      {!def.available && (
        <span className="text-micro text-warn">
          {def.unavailable_reason === 'kind_not_deliverable'
            ? 'sem entrega ao agente'
            : unavailableReasonText(def.unavailable_reason)}
        </span>
      )}
    </button>
  )

  if (def.available) return button

  // Botão desabilitado não recebe ponteiro nem foco: o gatilho do tooltip é o
  // invólucro, focável, para o motivo chegar também a quem usa teclado.
  return (
    <Tooltip content={unavailableReasonText(def.unavailable_reason)}>
      <span tabIndex={0} className="block cursor-not-allowed" aria-label={`${def.label}: indisponível`}>
        {button}
      </span>
    </Tooltip>
  )
}

/**
 * O diálogo de emissão (§6.4).
 *
 * Tipo sem entrega (`kind_not_deliverable`): `acknowledgeNotDeliverable` é um
 * checkbox que o operador MARCA, não uma constante do cliente — sem ele o POST
 * responde 409 e nada é persistido, de propósito: num EDR, um operador que
 * acredita ter isolado um host que segue na rede é um incidente de segurança. O
 * botão diz "Registrar comando", não "Isolar". Tipo entregue (hoje só
 * `restore_host`): sem checkbox, e o toast diz "enviado", nunca "feito".
 */
function IssueCommandDialog({
  endpointId,
  definition,
  warning,
  onClose,
  onIssued,
}: {
  endpointId: string
  definition: CommandDefinition
  warning: string | null
  onClose: () => void
  onIssued: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const { isAdmin } = useAuth()

  const [reason, setReason] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  // Hoje só `system_snapshot` é oferecido (o que o agente suporta primeiro).
  const [artifact, setArtifact] = useState('system_snapshot')
  const [target, setTarget] = useState('')

  const isCollect = definition.kind === 'collect_artifact'
  // O catálogo diz o que não tem entrega; o que ele oferece disponível, é entregue.
  const deliverable = definition.unavailable_reason !== 'kind_not_deliverable'

  // Conter é staff; descontener exige administrador — lido do catálogo, não
  // replicado numa tabela do cliente (§6.2).
  const adminBlocked = definition.requires_admin && !isAdmin

  // Comandos de IP precisam do alvo: sem ele o agente recebe `params` vazio.
  const isIpKind = isIpCommandKind(definition.kind)
  const isCidrKind = definition.kind === 'allow_ip' || definition.kind === 'revoke_allow_ip'
  const targetProblem = isIpKind ? (isCidrKind ? cidrProblem(target) : ipProblem(target)) : null
  const targetMissing = isIpKind && (target.trim() === '' || targetProblem !== null)

  // A coleta não leva justificativa (o Athena trata coleta como Low e não pede): exigi-la
  // aqui deixava o botão travado sem caminho para destravar.
  const reasonMissing = !isCollect && definition.requires_reason && reason.trim() === ''
  const disabled = (!deliverable && !acknowledged) || adminBlocked || reasonMissing || targetMissing

  const issueIp = (alvo: string, note: string, idempotencyKey: string) => {
    const optionalNote = note === '' ? undefined : note
    switch (definition.kind) {
      case 'block_ip':
        return machines.blockIp(endpointId, { ip: alvo, reason: note, idempotencyKey })
      case 'unblock_ip':
        return machines.unblockIp(endpointId, { ip: alvo, reason: optionalNote, idempotencyKey })
      case 'allow_ip':
        return machines.allowIp(endpointId, { cidr: alvo, note, idempotencyKey })
      default:
        return machines.revokeAllowIp(endpointId, { cidr: alvo, reason: optionalNote, idempotencyKey })
    }
  }

  const submit = (): void => {
    if (disabled) return
    action.run(async () => {
      try {
        // Uma chave por CLIQUE (§6.5): repetir a mesma chave devolve o comando
        // original, sem criar um segundo.
        const idempotencyKey = crypto.randomUUID()
        const command = isCollect
          ? // `POST /machines/{id}/commands` responde 405 em produção; a coleta vai pela
            // mesma rota de gestão dos demais comandos do catálogo.
            await admin.enqueueCommand(endpointId, {
              kind: 'collect_artifact',
              params: JSON.stringify({ artifact: 'system_snapshot' }),
              reason: null,
              idempotencyKey,
              acknowledgeNotDeliverable: false,
            })
          : isIpKind
            ? await issueIp(target.trim(), reason.trim(), idempotencyKey)
            : await admin.enqueueCommand(endpointId, {
              kind: definition.kind,
              params: null,
              reason: reason.trim() || null,
              idempotencyKey,
              acknowledgeNotDeliverable: !deliverable && acknowledged,
            })
        toast.success(
          deliverable
            ? `Enviado à fila do agente: ${definition.label}.`
            : `Comando registrado: ${definition.label}.`,
        )
        onIssued()
        void command
      } catch (cause) {
        // A mensagem carrega o id do comando aberto — navegue até ele em vez de
        // mostrar um erro sem saída (§6.5).
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            onClose()
            navigate(`/logs/${endpointId}?comando=${id}`)
            return
          }
        }
        throw cause
      }
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={definition.label}
      footer={
        <>
          {action.error !== null && (
            <p role="alert" className="mr-auto text-sm leading-snug text-crit">
              {describeError(action.error)}
            </p>
          )}
          {/* Sem entrega, o rótulo diz "Registrar comando", não "Isolar" (§6.4). */}
          <Button variant="danger" busy={action.busy} disabled={disabled} onClick={submit}>
            {deliverable ? definition.label : 'Registrar comando'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm leading-snug text-fg-secondary">{definition.description}</p>

        {definition.ttl_seconds !== null && (
          <p className="text-micro text-fg-muted">
            Válido por {formatMinutes(definition.ttl_seconds / 60)} após a entrega.
          </p>
        )}

        {warning !== null && <Notice tone="crit">{warning}</Notice>}

        {adminBlocked && (
          <Notice tone="warn">Este comando exige o papel de administrador.</Notice>
        )}

        {isCollect && (
          <>
            <SelectField
              label="Artefato"
              value={artifact}
              onValueChange={(v) => setArtifact(v as typeof artifact)}
              options={[
                { value: 'system_snapshot', label: 'Snapshot do sistema' },
              ]}
            />
          </>
        )}

        {isIpKind && (
          <label className="flex flex-col gap-1">
            <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
              {isCidrKind ? 'IP ou rede (CIDR)' : 'Endereço IP'}
              <span className="ml-1 text-fg-muted">*</span>
            </span>
            <input
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              maxLength={64}
              autoFocus
              spellCheck={false}
              className="w-full border border-line-strong bg-inset px-2 py-1 font-mono text-base text-fg placeholder:text-fg-muted"
              placeholder={isCidrKind ? 'ex.: 10.0.0.0/8' : 'ex.: 203.0.113.7'}
            />
            {targetProblem !== null && <span className="text-micro text-crit">{targetProblem}</span>}
          </label>
        )}

        {!isCollect && (
          <label className="flex flex-col gap-1">
            <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
              Justificativa
              {definition.requires_reason && <span className="ml-1 text-fg-muted">*</span>}
            </span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={512}
              rows={3}
              className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
              placeholder="ex.: contenção de intrusão ativa — ticket INC-4471"
            />
          </label>
        )}

        {!deliverable && (
          <label className="flex items-start gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 size-3.5 shrink-0"
              style={{ accentColor: 'var(--c-accent)' }}
            />
            <span>
              Entendo que este comando <strong>NÃO será entregue</strong> à máquina e ficará
              registrado como intenção.
            </span>
          </label>
        )}
      </div>
    </Dialog>
  )
}

function CommandHistory({
  endpointId,
  definitions,
  reloadKey,
  onUndo,
}: {
  endpointId: string
  definitions: Map<string, CommandDefinition>
  reloadKey: number
  onUndo: (def: CommandDefinition) => void
}) {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const [page, setPage] = useState(1)

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      admin.commandsForEndpoint(endpointId, { page, pageSize: 20 }, signal),
    // reloadKey força a releitura depois de emitir/cancelar.
    [endpointId, page, reloadKey],
  )
  const { data, error, loading, reload } = useResource(fetcher)

  // Enquanto algum comando depende do agente, a lista se atualiza sozinha.
  const awaiting = data?.items.some((command) => isAwaitingAgent(command.state)) ?? false
  useEffect(() => {
    if (!awaiting || loading) return
    const timer = setTimeout(reload, COMMAND_POLL_MS)
    return () => clearTimeout(timer)
  }, [awaiting, loading, reload])

  const cancel = useAction()
  const runCancel = (command: AgentCommand): void =>
    cancel.run(async () => {
      await admin.cancelCommand(command.id)
      toast.success('Comando cancelado.')
      reload()
    })

  return (
    <section>
      <h3 className="mb-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
        Histórico desta máquina
      </h3>

      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : data.items.length === 0 ? (
        <EmptyState title="Nenhum comando registrado para esta máquina." />
      ) : (
        <>
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Registrado
                </th>
                <th scope="col" className={TH}>
                  Comando
                </th>
                <th scope="col" className={TH}>
                  Situação
                </th>
                <th scope="col" className={TH}>
                  Por
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((command) => {
                const def = definitions.get(command.kind)
                // Desfazer só faz sentido depois que o agente CONFIRMOU a ação (§6.9).
                const reversedBy = def?.reversed_by ?? null
                const reversed =
                  reversedBy !== null && isConfirmed(command.state) ? definitions.get(reversedBy) : undefined
                const failure = command.state === 'failed' ? failureText(command) : null
                // O mesmo gate de emitir vale para cancelar (§6.2).
                const cancelBlocked = def?.requires_admin === true && !isAdmin

                return (
                  <tr key={command.id} className={TR}>
                    <td className={`${TD} tnum whitespace-nowrap`}>
                      {formatDayClock(command.requested_at)}
                    </td>
                    <td className={TD}>{def?.label ?? command.kind}</td>
                    <td className={TD}>
                      {/* `state`, não `status`: entregue ≠ executado. */}
                      <span className="flex flex-col items-start gap-0.5">
                        <CommandStateBadge state={command.state} />
                        {failure !== null && (
                          <span className="max-w-64 text-micro leading-snug break-words text-crit">{failure}</span>
                        )}
                      </span>
                    </td>
                    <td className={`${TD} text-fg-muted`}>{command.requested_by ?? '—'}</td>
                    <td className={`${TD} text-right`}>
                      <div className="flex justify-end gap-1">
                        {/* Use is_cancellable, não infira do status (§6.8). */}
                        {command.is_cancellable && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={cancelBlocked || cancel.busy}
                            title={cancelBlocked ? 'Cancelar exige administrador' : undefined}
                            onClick={() => runCancel(command)}
                          >
                            Cancelar
                          </Button>
                        )}
                        {/* Desfazer uma ação entregue é um comando NOVO (§6.9). */}
                        {!command.is_cancellable && reversed !== undefined && reversed.available && (
                          <Button size="sm" onClick={() => onUndo(reversed)}>
                            {reversed.label}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <Pagination
            {...adminPageProps(data)}
            busy={loading}
            onPageChange={setPage}
          />
        </>
      )}

      {cancel.error !== null && (
        <p role="alert" className="mt-2 text-sm text-crit">
          {describeError(cancel.error)}
        </p>
      )}
    </section>
  )
}
